import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { useCreateAdminInvitationMutation } from '../store/hooks';

interface AdminInviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  organizationId: string;
}

const AVAILABLE_ROLES = [
  ROLES.CLINICIAN,
  ROLES.OPERATIONS_SUPPORT,
  ROLES.PRACTICE_ADMINISTRATOR,
  ROLES.BILLING_ADMINISTRATOR,
  ROLES.SUPERVISOR,
];

export function AdminInviteUserDialog({
  open,
  onClose,
  onSuccess,
  organizationId,
}: AdminInviteUserDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createInvitation, { isLoading }] = useCreateAdminInvitationMutation();

  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>(ROLES.CLINICIAN);

  const handleInvite = useCallback(async () => {
    if (!email.trim()) {
      enqueueSnackbar('Please enter an email address', { variant: 'warning' });
      return;
    }

    try {
      await createInvitation({
        organizationId,
        adminSendInvitationRequest: {
          email: email.trim(),
          roles: [selectedRole],
        },
      }).unwrap();

      enqueueSnackbar('Invitation sent successfully', { variant: 'success' });
      setEmail('');
      setSelectedRole(ROLES.CLINICIAN);
      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to send invitation', { variant: 'error' });
    }
  }, [email, selectedRole, organizationId, createInvitation, enqueueSnackbar, onSuccess]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setEmail('');
      setSelectedRole(ROLES.CLINICIAN);
      onClose();
    }
  }, [isLoading, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User to Organization</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            autoFocus
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={isLoading}
            onKeyDown={e => {
              if (e.key === 'Enter' && !isLoading) {
                handleInvite();
              }
            }}
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={e => setSelectedRole(e.target.value)}
              label="Role"
              disabled={isLoading}
            >
              {AVAILABLE_ROLES.map(role => (
                <MenuItem key={role} value={role}>
                  {getRoleDisplayName(role)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleInvite}
          color="primary"
          variant="contained"
          disabled={isLoading || !email.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
